import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineRefresh, HiOutlineDownload } from 'react-icons/hi';
import { UserContext } from "../UserContext";
import apiClient from '../../api/client';
import Header from '../Header';
import { providerLabel, formatRupees, formatDateTime } from '../../utils/providers';
import './legacy.css';
import './index.css';
import LoadError from '../LoadError';
import { getErrorMessage } from '../../utils/network';

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const TYPES = [
  { key: '', label: 'All' },
  { key: 'credit', label: 'Money added' },
  { key: 'debit', label: 'Recharges' },
];

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const customerName = (t) => `${t.customer_name || ''} ${t.customer_last_name || ''}`.trim();

/**
 * Wallet & recharge history for the logged-in account.
 *
 * Changes from the previous version:
 *  - real current balance from the server (was guessed from the newest row)
 *  - totals for the selected period: money added, spent on recharges, net
 *  - Today / Yesterday / week / month / last month / custom range,
 *    calculated in India time on the server
 *  - filter by type and provider, search by customer / phone / plan / remarks
 *  - customer name opens their page on the Transactions tab
 *  - CSV export of exactly what's filtered
 *  - card layout on phones; no crash when remarks are empty
 */
const WalletHistory = () => {
  const { user } = useContext(UserContext);
  const isSuperadmin = user?.role === 'superadmin';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCause, setErrorCause] = useState(null);

  const [period, setPeriod] = useState('month');
  const [from, setFrom] = useState(todayYmd());
  const [to, setTo] = useState(todayYmd());
  const [type, setType] = useState('');
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchHistory = useCallback(async () => {
    if (period === 'custom' && (!from || !to || from > to)) return;
    setLoading(true);
    setError(null);
    setErrorCause(null);
    try {
      const params = new URLSearchParams({ filter: period });
      if (period === 'custom') { params.set('from', from); params.set('to', to); }
      if (type) params.set('type', type);
      const { data } = await apiClient.get(`/getWalletHistory.php?${params}`);
      if (!data.success) throw new Error(data.message || 'Could not load history');
      setRows(data.data || []);
      setSummary(data.summary || null);
      setTruncated(!!data.truncated);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch history'));
      setErrorCause(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period, from, to, type]);

  useEffect(() => {
    fetchHistory();
    setPage(1);
  }, [fetchHistory]);

  const providerOptions = useMemo(
    () => [...new Set(rows.map((r) => r.recharge_type).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (provider && r.recharge_type !== provider) return false;
      if (!q) return true;
      return [customerName(r), r.customer_phone, r.plan_name, r.remarks, r.account_name, providerLabel(r.recharge_type)]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [rows, provider, search]);

  useEffect(() => setPage(1), [provider, search, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(startIndex, startIndex + pageSize);

  // When provider/search narrows the list, recompute totals for what's visible.
  const narrowed = !!(provider || search.trim());
  const visibleTotals = useMemo(() => {
    const t = { credit: 0, debit: 0, creditCount: 0, debitCount: 0 };
    filtered.forEach((r) => {
      if (String(r.transaction_type).toLowerCase() === 'credit') { t.credit += Number(r.amount) || 0; t.creditCount++; }
      else { t.debit += Number(r.amount) || 0; t.debitCount++; }
    });
    return t;
  }, [filtered]);

  const credit = narrowed ? visibleTotals.credit : summary?.total_credit ?? 0;
  const debit = narrowed ? visibleTotals.debit : summary?.total_debit ?? 0;
  const creditCount = narrowed ? visibleTotals.creditCount : summary?.credit_count ?? 0;
  const debitCount = narrowed ? visibleTotals.debitCount : summary?.debit_count ?? 0;

  const exportCsv = () => {
    const header = ['Date', 'Type', 'Amount', 'Balance after', 'Customer', 'Phone', 'Provider', 'Plan', ...(isSuperadmin ? ['Account'] : []), 'Remarks'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = filtered.map((r) => [
      formatDateTime(r.created_at_ist || r.created_at), r.transaction_type, r.amount, r.balance_after,
      customerName(r), r.customer_phone, providerLabel(r.recharge_type), r.plan_name,
      ...(isSuperadmin ? [r.account_name] : []), r.remarks,
    ].map(esc).join(','));
    const blob = new Blob(['﻿' + [header.map(esc).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wallet-history-${period === 'custom' ? `${from}_to_${to}` : period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const periodLabel = PERIODS.find((p) => p.key === period)?.label;

  const renderCustomer = (r) => {
    const name = customerName(r);
    if (!r.customer_id && !name) return <span className="wh-muted">{String(r.transaction_type).toLowerCase() === 'credit' ? 'Wallet top-up' : '—'}</span>;
    return (
      <div className="wh-customer">
        {r.customer_id ? (
          <Link to={`/customer/${r.customer_id}?tab=transactions`} title="Open customer">{name || `Customer #${r.customer_id}`}</Link>
        ) : name}
        {r.customer_phone && <a className="wh-phone" href={`tel:${r.customer_phone}`}>{r.customer_phone}</a>}
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="wh-page">
        <div className="wh-head">
          <div>
            <h2>Wallet &amp; Recharge History</h2>
            <p>{user?.operatorName || 'Your account'} · {periodLabel}{period === 'custom' ? ` (${from} → ${to})` : ''}</p>
          </div>
          <div className="wh-head-actions">
            <button type="button" className="wh-btn" onClick={fetchHistory} disabled={loading}>
              <HiOutlineRefresh /> <span>Refresh</span>
            </button>
            <button type="button" className="wh-btn wh-btn--primary" onClick={exportCsv} disabled={loading || filtered.length === 0}>
              <HiOutlineDownload /> <span>Export CSV</span>
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="wh-summary">
          <div className="wh-stat wh-stat--balance">
            <span className="wh-stat__label">Current balance</span>
            <span className="wh-stat__value">{summary ? formatRupees(summary.current_wallet) : '—'}</span>
            <span className="wh-stat__hint">right now</span>
          </div>
          <div className="wh-stat wh-stat--credit">
            <span className="wh-stat__label">Money added</span>
            <span className="wh-stat__value">{loading ? '…' : formatRupees(credit)}</span>
            <span className="wh-stat__hint">{creditCount} top-up{creditCount === 1 ? '' : 's'}</span>
          </div>
          <div className="wh-stat wh-stat--debit">
            <span className="wh-stat__label">Spent on recharges</span>
            <span className="wh-stat__value">{loading ? '…' : formatRupees(debit)}</span>
            <span className="wh-stat__hint">{debitCount} recharge{debitCount === 1 ? '' : 's'}</span>
          </div>
          <div className="wh-stat">
            <span className="wh-stat__label">Net change</span>
            <span className={`wh-stat__value ${credit - debit < 0 ? 'neg' : 'pos'}`}>
              {loading ? '…' : `${credit - debit < 0 ? '−' : '+'}${formatRupees(Math.abs(credit - debit))}`}
            </span>
            <span className="wh-stat__hint">{narrowed ? 'for filtered rows' : periodLabel?.toLowerCase()}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="wh-filters">
          <div className="wh-chips" role="tablist" aria-label="Time period">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={period === p.key}
                className={`wh-chip ${period === p.key ? 'active' : ''}`}
                onClick={() => setPeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {period === 'custom' && (
            <div className="wh-range">
              <label>From <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></label>
              <label>To <input type="date" value={to} min={from} max={todayYmd()} onChange={(e) => setTo(e.target.value)} /></label>
            </div>
          )}

          <div className="wh-filter-row">
            <div className="wh-seg" role="tablist" aria-label="Transaction type">
              {TYPES.map((t) => (
                <button
                  key={t.key || 'all'}
                  type="button"
                  className={type === t.key ? 'active' : ''}
                  onClick={() => setType(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <select className="wh-input" value={provider} onChange={(e) => setProvider(e.target.value)} aria-label="Provider">
              <option value="">All providers</option>
              {providerOptions.map((p) => <option key={p} value={p}>{providerLabel(p)}</option>)}
            </select>

            <input
              type="search"
              className="wh-input wh-search"
              placeholder="Search customer, phone, plan, remarks…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {truncated && (
          <div className="wh-notice">Showing the latest 5,000 entries for this period. Pick a shorter period to see everything.</div>
        )}

        {loading ? (
          <div className="wh-skeleton">
            {[...Array(6)].map((_, i) => <div key={i} className="wh-skeleton__row" />)}
          </div>
        ) : error ? (
          <LoadError error={errorCause} message={error} onRetry={fetchHistory} />
        ) : filtered.length === 0 ? (
          <div className="wh-empty">
            <p>No transactions for {narrowed ? 'these filters' : periodLabel?.toLowerCase()}.</p>
            {period !== 'all' && <button type="button" className="wh-btn" onClick={() => setPeriod('all')}>Show all time</button>}
          </div>
        ) : (
          <>
            {/* Table (tablet / desktop) */}
            <div className="wh-table-wrap">
              <table className="wh-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Provider · Plan</th>
                    {isSuperadmin && <th>Account</th>}
                    <th className="num">Amount</th>
                    <th className="num">Balance after</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r, i) => {
                    const isCredit = String(r.transaction_type).toLowerCase() === 'credit';
                    return (
                      <tr key={r.id || `${r.created_at}-${i}`} className={isCredit ? 'is-credit' : 'is-debit'}>
                        <td className="nowrap">{formatDateTime(r.created_at_ist || r.created_at)}</td>
                        <td>{renderCustomer(r)}</td>
                        <td>
                          {r.recharge_type ? (
                            <>
                              <span className="wh-badge">{providerLabel(r.recharge_type)}</span>
                              {r.plan_name && <span className="wh-plan">{r.plan_name}</span>}
                            </>
                          ) : (
                            <span className={`wh-type ${isCredit ? 'credit' : 'debit'}`}>{isCredit ? 'Credit' : 'Debit'}</span>
                          )}
                        </td>
                        {isSuperadmin && <td>{r.account_name || '—'}</td>}
                        <td className={`num wh-amount ${isCredit ? 'credit' : 'debit'}`}>
                          {isCredit ? '+' : '−'}{formatRupees(r.amount)}
                        </td>
                        <td className="num">{r.balance_after != null ? formatRupees(r.balance_after) : '—'}</td>
                        <td className="wh-remarks" title={r.remarks || ''}>{r.remarks || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Cards (phones) */}
            <ul className="wh-cards">
              {pageRows.map((r, i) => {
                const isCredit = String(r.transaction_type).toLowerCase() === 'credit';
                return (
                  <li key={r.id || `${r.created_at}-${i}`} className={`wh-card ${isCredit ? 'is-credit' : 'is-debit'}`}>
                    <div className="wh-card__top">
                      <span className={`wh-amount ${isCredit ? 'credit' : 'debit'}`}>
                        {isCredit ? '+' : '−'}{formatRupees(r.amount)}
                      </span>
                      <span className="wh-muted">{formatDateTime(r.created_at_ist || r.created_at)}</span>
                    </div>
                    {renderCustomer(r)}
                    {r.recharge_type && (
                      <div><span className="wh-badge">{providerLabel(r.recharge_type)}</span>{r.plan_name && <span className="wh-plan">{r.plan_name}</span>}</div>
                    )}
                    {isSuperadmin && r.account_name && <div className="wh-muted">Account: {r.account_name}</div>}
                    {r.remarks && <div className="wh-muted wh-card__remarks">{r.remarks}</div>}
                    {r.balance_after != null && <div className="wh-muted">Balance after: {formatRupees(r.balance_after)}</div>}
                  </li>
                );
              })}
            </ul>

            <div className="wh-pager">
              <span className="wh-muted">
                {startIndex + 1}–{Math.min(startIndex + pageSize, filtered.length)} of {filtered.length.toLocaleString('en-IN')}
              </span>
              <div className="wh-pager__controls">
                <select className="wh-input" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} aria-label="Rows per page">
                  {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <button type="button" className="wh-btn" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>‹ Prev</button>
                <span className="wh-muted">Page {safePage} of {totalPages}</span>
                <button type="button" className="wh-btn" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next ›</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default WalletHistory;
